// Performance rolled up by tag: the Insights tab's arithmetic.
//
// Input is the Performance tab's own per-creative rows (GET
// /v1/creative-performance/ads?groupBy=creative) joined to each creative's tags
// (GET /v1/creative-insights/creatives) on creative_key. Rolling up here rather
// than in a second SQL query means a group's numbers are by construction the sum
// of the creative rows shown under it, and the dimension switch costs no request.
//
// Every rate is re-derived from summed numerators and denominators, exactly as
// creativePerformanceQueries.js does it, so a group is never a mean of means.
import { CreativeRow } from '../types';
import { humanize, TaggedCreative, VocabValue } from '../tags/types';
import { toNum } from '../utils';

export const DIMENSION = {
  PERSONA: 'persona',
  LEAD_SELLING_POINT: 'lead_selling_point',
  HOOK: 'hook_device',
  ANGLE: 'persuasion_angle',
  FORMAT: 'format',
  PRESENTER: 'presenter',
  OFFER: 'offer_type',
  AWARENESS: 'awareness_level',
  LANDING_PAGE: 'landing_page',
  PRODUCT: 'product',
  MEDIA: 'media_type',
} as const;
export type Dimension = (typeof DIMENSION)[keyof typeof DIMENSION];

export const DIMENSION_LABELS: Record<Dimension, string> = {
  persona: 'Persona',
  lead_selling_point: 'Lead selling point',
  hook_device: 'Hook',
  persuasion_angle: 'Angle',
  format: 'Format',
  presenter: 'Presenter',
  offer_type: 'Offer',
  awareness_level: 'Awareness',
  landing_page: 'Landing page',
  product: 'Product',
  media_type: 'Media type',
};

// Buckets for creatives that have no value for the chosen dimension, kept as
// their own rows so the groups always add up to the whole account.
export const NOT_TAGGED = '__not_tagged__';
export const NO_MATCH = '__no_match__';

// The additive measures. Everything else is derived from these.
const SUM_FIELDS = [
  'spend',
  'revenue',
  'profit',
  'orders',
  'nc_orders',
  'nc_revenue',
  'sessions',
  'add_to_carts',
  'bounces',
  'impressions',
  'clicks',
  'meta_purchases',
  'meta_purchase_value',
  'video_impressions',
  'video_thruplays',
  'video_p25',
  'video_p100',
] as const;
type SumField = (typeof SUM_FIELDS)[number];

export interface GroupMetrics extends Record<SumField, number | null> {
  ad_count: number;
  creative_count: number;
  spend_share: number | null;
  roas: number | null;
  poas: number | null;
  cpa: number | null;
  aov: number | null;
  nc_roas: number | null;
  nc_cpa: number | null;
  cvr: number | null;
  atc_rate: number | null;
  ctr: number | null;
  cpm: number | null;
  meta_roas: number | null;
  true_revenue: number | null;
  true_roas: number | null;
  true_profit: number | null;
  hold_rate: number | null;
  thruplay_rate: number | null;
}

export interface InsightRow extends Partial<GroupMetrics> {
  id: string;
  path: string[];
  kind: 'group' | 'creative';
  label: string;
  groupKey: string;
  // Creative rows only.
  creative?: CreativeRow;
  tagged?: TaggedCreative;
}

const ratio = (num: number | null, den: number | null, scale = 1) =>
  num != null && den != null && den > 0 ? (num / den) * scale : null;

/** Sum a set of creative rows and derive every rate from the sums. */
export function sumMetrics(
  rows: CreativeRow[],
  multiplier: number | null,
  totalSpend: number,
): GroupMetrics {
  const sums = {} as Record<SumField, number | null>;
  for (const field of SUM_FIELDS) {
    let total: number | null = null;
    for (const row of rows) {
      const v = toNum((row as unknown as Record<string, unknown>)[field]);
      if (v != null) total = (total ?? 0) + v;
    }
    sums[field] = total;
  }
  const trueRevenue =
    multiplier != null && sums.meta_purchase_value != null
      ? sums.meta_purchase_value * multiplier
      : null;
  // True profit is the one measure NOT re-derived from sums: each creative's
  // figure charges its own uplift at its own Triple Whale margin, and is null
  // when it has no Meta purchases. Summing those per-creative figures is what the
  // Performance tab's totals do (summarize() in the API), so a group equals the
  // creatives under it; one margin across the whole group would not.
  let trueProfit: number | null = null;
  for (const row of rows) {
    const v = toNum(row.true_profit);
    if (v != null) trueProfit = (trueProfit ?? 0) + v;
  }
  return {
    ...sums,
    ad_count: rows.reduce((n, r) => n + (Number(r.ad_count) || 0), 0),
    creative_count: rows.length,
    spend_share: ratio(sums.spend, totalSpend),
    roas: ratio(sums.revenue, sums.spend),
    poas: ratio(sums.profit, sums.spend),
    cpa: ratio(sums.spend, sums.orders),
    aov: ratio(sums.revenue, sums.orders),
    nc_roas: ratio(sums.nc_revenue, sums.spend),
    nc_cpa: ratio(sums.spend, sums.nc_orders),
    cvr: ratio(sums.orders, sums.sessions),
    atc_rate: ratio(sums.add_to_carts, sums.sessions),
    ctr: ratio(sums.clicks, sums.impressions),
    cpm: ratio(sums.spend, sums.impressions, 1000),
    meta_roas: ratio(sums.meta_purchase_value, sums.spend),
    true_revenue: trueRevenue,
    true_roas: ratio(trueRevenue, sums.spend),
    true_profit: trueProfit,
    hold_rate: ratio(sums.video_p25, sums.video_impressions),
    thruplay_rate: ratio(sums.video_thruplays, sums.video_impressions),
  };
}

/** A landing page as one value: host + path, lowercased, no query/fragment/trailing slash. */
export function normalizeLandingPage(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '');
    return `${u.hostname.replace(/^www\./, '')}${path}`.toLowerCase();
  } catch (err) {
    return url.toLowerCase();
  }
}

interface Keyed {
  key: string;
  label: string;
}

type LabelLookup = (
  dimension: 'persona' | 'selling_point',
  product: string,
  key: string,
) => string;

export function makeLabelLookup(vocabulary: VocabValue[]): LabelLookup {
  const byKey = new Map<string, string>();
  for (const v of vocabulary)
    byKey.set(`${v.dimension}|${v.product || ''}|${v.value_key}`, v.label);
  return (dimension, product, key) =>
    byKey.get(
      `${dimension}|${dimension === 'persona' ? '' : product}|${key}`,
    ) || humanize(key);
}

/** Which group a creative falls in for the chosen dimension. */
export function groupFor(
  dimension: Dimension,
  row: CreativeRow,
  tagged: TaggedCreative | undefined,
  labelOf: LabelLookup,
  multipleProducts: boolean,
): Keyed {
  if (dimension === DIMENSION.LANDING_PAGE) {
    const page = normalizeLandingPage(row.destination_url);
    return page
      ? { key: page, label: page }
      : { key: NO_MATCH, label: 'No landing page recorded' };
  }
  if (dimension === DIMENSION.MEDIA) {
    return { key: row.media_type, label: humanize(row.media_type) };
  }
  if (!tagged || tagged.status !== 'done')
    return { key: NOT_TAGGED, label: 'Not tagged yet' };
  if (dimension === DIMENSION.PRODUCT)
    return { key: tagged.product, label: tagged.product };

  const value = tagged.tags[dimension as keyof TaggedCreative['tags']] as
    string | null;
  if (!value) {
    const vocab =
      dimension === DIMENSION.PERSONA ||
      dimension === DIMENSION.LEAD_SELLING_POINT;
    return {
      key: NO_MATCH,
      label: vocab ? 'No approved match yet' : 'Not classified',
    };
  }
  if (dimension === DIMENSION.PERSONA)
    return { key: value, label: labelOf('persona', tagged.product, value) };
  if (dimension === DIMENSION.LEAD_SELLING_POINT) {
    // Selling points belong to a product, so the same key under two products
    // is two different claims.
    const label = labelOf('selling_point', tagged.product, value);
    return {
      key: `${tagged.product}|${value}`,
      label: multipleProducts ? `${label} (${tagged.product})` : label,
    };
  }
  if (dimension === DIMENSION.FORMAT) {
    // Video and static formats are separate vocabularies.
    const medium = tagged.is_video ? 'Video' : 'Static';
    return {
      key: `${medium}|${value}`,
      label: `${medium}: ${humanize(value).toLowerCase()}`,
    };
  }
  return { key: value, label: humanize(value) };
}

/**
 * The tree the grid renders: one group row per value, each followed by its
 * creatives. Groups are ordered by spend; the "not tagged" and "no match"
 * buckets always sort last.
 */
export function buildInsightRows(
  dimension: Dimension,
  creatives: CreativeRow[],
  tagsByKey: Map<string, TaggedCreative>,
  vocabulary: VocabValue[],
  multiplier: number | null,
): { rows: InsightRow[]; totalSpend: number } {
  const labelOf = makeLabelLookup(vocabulary);
  const products = new Set(
    creatives.map(c => tagsByKey.get(c.group_key)?.product).filter(Boolean),
  );
  const totalSpend = creatives.reduce((s, c) => s + (toNum(c.spend) || 0), 0);

  const groups = new Map<string, { label: string; members: CreativeRow[] }>();
  for (const creative of creatives) {
    const tagged = tagsByKey.get(creative.group_key);
    const { key, label } = groupFor(
      dimension,
      creative,
      tagged,
      labelOf,
      products.size > 1,
    );
    const group = groups.get(key) || { label, members: [] };
    group.members.push(creative);
    groups.set(key, group);
  }

  const special = (key: string) => key === NOT_TAGGED || key === NO_MATCH;
  const ordered = [...groups.entries()]
    .map(([key, g]) => ({
      key,
      ...g,
      metrics: sumMetrics(g.members, multiplier, totalSpend),
    }))
    .sort(
      (a, b) =>
        Number(special(a.key)) - Number(special(b.key)) ||
        (b.metrics.spend || 0) - (a.metrics.spend || 0),
    );

  const rows: InsightRow[] = [];
  for (const g of ordered) {
    // Path segments must be unique per level; the key, not the label, guarantees it.
    const groupPath = `${g.label}\u0000${g.key}`;
    rows.push({
      id: `g:${g.key}`,
      path: [groupPath],
      kind: 'group',
      label: g.label,
      groupKey: g.key,
      ...g.metrics,
    });
    for (const creative of g.members) {
      rows.push({
        id: `c:${g.key}:${creative.group_key}`,
        path: [groupPath, creative.group_key],
        kind: 'creative',
        label: creative.ad_name || creative.group_key,
        groupKey: g.key,
        creative,
        tagged: tagsByKey.get(creative.group_key),
        ...sumMetrics([creative], multiplier, totalSpend),
      });
    }
  }
  return { rows, totalSpend };
}
